import type {
  GASetupDatalayer,
  GASetupDatalayerParamItems,
  GASetupDatalayerParamPage,
  GASetupDatalayerParamUser,
  GASetupDatalayerParamWishlist,
} from '@integration-layer/schemas/GAEvents/dataLayerSetup'

/**
 * Provides utility functions for resetting and mapping default parameters for Google Analytics tracking.
 *
 * @returns {Object} An object containing default values and parameter mapping functions.
 *
 * @description This composable helps standardize and reset tracking parameters across different
 * sections of the application, ensuring consistent data reporting to Google Analytics.
 *
 * @example
 * const { defaultValues, setParamUser, setParamItems } = useResetParams()
 * const userParams = setParamUser({ userLogged: 1, userID: '123' })
 */
export const useResetParams = () => {
  /**
   * Default values for various tracking parameters across different sections.
   *
   * @type {Omit<GASetupDatalayer, 'event'>}
   * @description Provides comprehensive default values for global, user, page, cart, wishlist, sales, and store parameters.
   */
  const defaultValues: Omit<GASetupDatalayer, 'event'> = {
    paramGlobal: {
      environment: 'development',
      device: 'm',
      current_shipping: '',
      website_language: '',
      selected_currency: '',
      localDate: '',
      local_hour_minute: '',
      local_parts_of_day: 'morning',
      local_office_hours: 'office',
      timestamp: '',
    },
    paramUser: {
      userLogged: 0,
      userID: '',
      registrationDate: '',
      userType: '',
      userAge: '',
      userGender: 'unknown',
      cluster_age: '',
      SHA256_hashed_userEmail: '',
      registeredNewsletter: 0,
      userEmail: '',
      userName: '',
      userPhone: '',
      userSurname: '',
    },
    paramPage: {
      page_status_code: 200,
      sub_page_type: '',
      department: '',
      brand: 'armani_group',
      gender: 'E',
      page_type: '',
    },
    paramItems: {
      itemOutOfStock: 0,
      storeAvailability: 0,
      item_category_level_1: '',
      item_category_level_2: '',
      shop_category: 'category',
      product_listSize: 0,
      product_listtype: 'grid432',
    },
    paramCart: {
      cartID: '',
      cart_type: 'mono_brand',
      emporio_armani_cart: 0,
      armani_exchange_cart: 0,
      ea7_cart: 0,
      giorgio_armani_cart: 0,
      client_service_cart: 0,
      cart_products: 0,
      cartTotalValue: '0.00',
    },
    paramWishlist: {
      armani_exchange_wishlist: 0,
      ea7_wishlist: 0,
      emporio_armani_wishlist: 0,
      giorgio_armani_wishlist: 0,
      wishlist_products: 0,
      wishlistTotalValue: 0,
    },
    paramSales: {
      checkout_type: 0,
      payment_type: '',
      delivery_type: '',
    },
    paramStore: {
      store: '',
      city_store: '',
    },
  }

  /**
   * Maps an object with default values, ensuring all required fields are present.
   *
   * @template T The type of the object being mapped
   * @param {T | undefined} obj The input object to map
   * @param {T} defaults The default values to use
   * @returns {T} A fully populated object with default values
   *
   * @description Fills in missing fields with default values, preventing undefined errors.
   * @private
   */
  const mapWithDefaults = <T extends Record<string, unknown>>(
    obj: T | undefined,
    defaults: T
  ): T => {
    if (obj === undefined) {
      return defaults as T
    }
    const result: Partial<T> = { ...defaults }
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        result[key] = obj[key] ?? defaults[key]
      }
    }
    return result as T
  }

  /**
   * Sets user parameters with default fallback values.
   *
   * @param {GASetupDatalayerParamUser} payload User tracking parameters
   * @returns {GASetupDatalayerParamUser} Mapped user parameters
   */
  const setParamUser = (
    payload: GASetupDatalayerParamUser
  ): GASetupDatalayerParamUser =>
    mapWithDefaults(payload, defaultValues.paramUser)

  /**
   * Sets wishlist parameters with default fallback values.
   *
   * @param {GASetupDatalayerParamWishlist} payload Wishlist tracking parameters
   * @returns {GASetupDatalayerParamWishlist} Mapped wishlist parameters
   */
  const setParamWishlist = (
    payload: GASetupDatalayerParamWishlist
  ): GASetupDatalayerParamWishlist =>
    mapWithDefaults(payload, defaultValues.paramWishlist)

  /**
   * Sets item parameters with default fallback values.
   *
   * @param {GASetupDatalayerParamItems} payload Item tracking parameters
   * @returns {GASetupDatalayerParamItems} Mapped item parameters
   */
  const setParamItems = (
    payload: GASetupDatalayerParamItems
  ): GASetupDatalayerParamItems =>
    mapWithDefaults(payload, defaultValues.paramItems)

  /**
   * Sets page parameters with default fallback values.
   *
   * @param {GASetupDatalayerParamPage | undefined} payload Page tracking parameters
   * @returns {GASetupDatalayerParamPage} Mapped page parameters
   */
  const setParamPages = (
    payload: GASetupDatalayerParamPage | undefined
  ): GASetupDatalayerParamPage =>
    mapWithDefaults(payload, defaultValues.paramPage)

  return {
    defaultValues,
    setParamUser,
    setParamWishlist,
    setParamItems,
    setParamPages,
  }
}
